<template>
  <list-page icone="mdi-binoculars" titulo="Captação" @novo="Novo" @atualizar="Listar" :temBuscaRapida="true" :buscaRapida.sync="search">
    <template v-slot:botoes>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn icon color="primary" @click="tableToExcel('tabela')" :disabled="visualizacao !== 'lista'" v-on="on" aria-label="Exportar lista"><v-icon>mdi-file-excel</v-icon></v-btn>
        </template>
        <span>Exportar lista</span>
      </v-tooltip>
      <info />
    </template>
    <template v-slot:corpo>
      <v-expansion-panels class="mb-4">
        <v-expansion-panel>
          <v-expansion-panel-header>Filtros</v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row dense>
              <v-col cols="12" md="4"><v-autocomplete v-model="filtro.doadorId" :items="doadores" :search-input.sync="buscaDoador" :loading="carregandoDoadores" item-text="nome" item-value="id" label="Doador" outlined dense clearable no-filter /></v-col>
              <v-col cols="12" md="4"><v-autocomplete v-model="filtro.responsavelId" :items="responsaveis" item-text="nome" item-value="id" label="Responsável" outlined dense clearable /></v-col>
              <v-col cols="12" md="4"><v-select v-model="filtro.probabilidade" :items="etapasPadrao" item-text="rotulo" item-value="probabilidade" label="Etapa / probabilidade" outlined dense clearable /></v-col>
              <v-col cols="12" md="4"><v-select v-model="filtro.statusId" :items="status" item-text="nome" item-value="id" label="Status" outlined dense clearable /></v-col>
              <v-col cols="12" md="4"><v-select v-model="filtro.fonteId" :items="fontes" item-text="value" item-value="key" label="Fonte" outlined dense clearable /></v-col>
              <v-col cols="12" md="4"><v-select v-model="filtro.incentivoId" :items="incentivos" item-text="value" item-value="key" label="Lei de incentivo" outlined dense clearable /></v-col>
              <v-col cols="12" md="4"><v-text-field v-model="filtro.previsaoInicio" type="date" label="Previsão de fechamento de" outlined dense clearable /></v-col>
              <v-col cols="12" md="4"><v-text-field v-model="filtro.previsaoFim" type="date" label="Previsão de fechamento até" outlined dense clearable /></v-col>
              <v-col cols="12" md="4"><v-checkbox v-model="filtro.atividadesAtrasadas" label="Com atividades atrasadas" class="mt-1" /></v-col>
            </v-row>
            <div class="d-flex justify-end"><v-btn text @click="LimparFiltros">Limpar</v-btn><v-btn color="primary" @click="Listar" :loading="loading">Consultar</v-btn></div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <v-alert v-if="erroCarga" type="error" outlined>{{ erroCarga }}</v-alert>
      <div class="indicadores mb-4" aria-live="polite">
        <v-card v-for="card in cardsIndicadores" :key="card.nome" outlined class="pa-3 indicador">
          <div class="text-caption">{{ card.nome }}</div><div class="text-h6">{{ loading ? '…' : card.valor }}</div>
        </v-card>
      </div>
      <v-btn-toggle v-model="visualizacao" mandatory color="primary" class="mb-4" aria-label="Visualização das captações">
        <v-btn value="lista" small>Lista</v-btn><v-btn value="kanban" small>Kanban</v-btn><v-btn value="funil" small>Funil</v-btn>
      </v-btn-toggle>
      <v-progress-linear v-if="loading || movimentando" indeterminate class="mb-3" />

      <v-data-table v-if="visualizacao === 'lista'" id="tabela" :headers="headers" :items="oportunidades" :items-per-page="15" :loading="loading" class="elevation-1">
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn icon small @click="Editar(item)" aria-label="Editar captação"><v-icon small>mdi-pencil</v-icon></v-btn>
          <v-btn icon small @click="Excluir(item)" aria-label="Excluir captação"><v-icon small>mdi-delete</v-icon></v-btn>
        </template>
        <template v-slot:[`item.valorTotal`]="{ item }">{{ Moeda(item.valorTotal) }}</template>
        <template v-slot:[`item.probabilidade`]="{ item }">{{ NomeEtapa(item.probabilidade) }}</template>
        <template v-slot:[`item.fonte`]="{ item }">{{ NomeEnum(fontes, item.fonte) }}</template>
        <template v-slot:[`item.status`]="{ item }">{{ NomeStatus(item.status) }}</template>
        <template v-slot:[`item.leiIncentivo`]="{ item }">{{ NomeEnum(incentivos, item.leiIncentivo) }}</template>
        <template v-slot:[`item.previsaoFechamento`]="{ item }">{{ Data(item.previsaoFechamento) }}</template>
        <template v-slot:[`item.proximaAtividade`]="{ item }"><span :class="{ 'error--text': item.proximaAtividade && item.proximaAtividade.atrasada }">{{ item.proximaAtividade ? item.proximaAtividade.descricao : '—' }}</span></template>
        <template v-slot:[`item.anexo`]="{ item }"><a v-if="item.anexo" @click="Download(item)">{{ item.anexo }}</a></template>
      </v-data-table>

      <div v-else-if="visualizacao === 'kanban'" class="kanban" aria-label="Funil de captação em colunas">
        <section v-for="etapa in etapas" :key="etapa.probabilidade" class="kanban-coluna" :style="{ borderTopColor: cores[etapa.probabilidade - 1] }" @dragover.prevent @drop.prevent="Soltar(etapa.probabilidade)">
          <div class="pa-3">
            <h3 class="subtitle-1 font-weight-bold">{{ etapa.nome }}</h3>
            <div class="text-caption">{{ etapa.quantidade }} oportunidade(s) · {{ Moeda(etapa.valorTotal) }}</div>
          </div>
          <v-card v-for="card in CardsEtapa(etapa.probabilidade)" :key="card.id" outlined class="mx-2 mb-3 pa-3 kanban-card" :draggable="!movimentando" @click="Editar(card)" @dragstart="Arrastar($event, card)" @dragend="arrastando = null">
            <div class="d-flex align-start">
              <button class="captacao-titulo text-left primary--text font-weight-bold" @click.stop="Editar(card)">{{ card.descricao }}</button>
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }"><v-btn icon small v-bind="attrs" v-on="on" @click.stop :disabled="movimentando" :aria-label="'Mover captação ' + card.descricao"><v-icon small>mdi-dots-vertical</v-icon></v-btn></template>
                <v-list dense><v-subheader>Mover para</v-subheader><v-list-item v-for="destino in etapasPadrao" :key="destino.probabilidade" :disabled="destino.probabilidade === card.probabilidade" @click="Mover(card, destino.probabilidade)"><v-list-item-title>{{ destino.rotulo }}</v-list-item-title></v-list-item></v-list>
              </v-menu>
            </div>
            <div class="text-body-2 mt-1">{{ card.doadorNome }}</div>
            <div class="font-weight-bold my-2">{{ Moeda(card.valorTotal) }}</div>
            <div class="text-caption">Probabilidade: {{ card.percentual }}%</div>
            <div class="text-caption">Previsão: {{ Data(card.previsaoFechamento) }}</div>
            <div class="text-caption">Responsável: {{ card.responsavelNome || '—' }}</div>
            <div v-if="card.proximaAtividade" class="text-body-2 mt-2" :class="{ 'error--text': card.proximaAtividade.atrasada }">
              <v-icon v-if="card.proximaAtividade.atrasada" small color="error">mdi-clock-alert-outline</v-icon>
              {{ card.proximaAtividade.descricao }}<div class="text-caption">{{ Data(card.proximaAtividade.dataPrevista) }} {{ (card.proximaAtividade.horaPrevista || '').slice(0, 5) }}</div>
            </div>
            <v-chip v-if="card.atividadesAtrasadas" color="error" small outlined class="mt-2">{{ card.atividadesAtrasadas }} atividade(s) atrasada(s)</v-chip>
          </v-card>
          <p v-if="!etapa.quantidade" class="text-caption px-3">Nenhuma captação nesta etapa.</p>
        </section>
      </div>
      <v-card v-else flat class="pa-3"><funel-chart id="crm-funil-captacao" title="Captação" :headers="etapas.map(e => e.nome)" :data="dadosFunil" :text="etapas.map(e => e.valorTotal)" /></v-card>
      <cadastro-oportunidade v-model="dialogCadastro" :item="item" @fechou="dialogCadastro = false" @salvou="Listar" @atualizado="Listar" />
    </template>
  </list-page>
</template>

<script>
import OportunidadeService from '@/core/services/doacao/OportunidadeService';
import DoadorService from '@/core/services/doacao/DoadorService';
import Oportunidade from '@/core/domain/doacao/Oportunidade';
import OportunidadeFonteEnumService from '@/core/services/doacao/OportunidadeFonteEnumService';
import OportunidadeLeiIncentivoEnumService from '@/core/services/doacao/OportunidadeLeiIncentivoEnumService';
import { TableToExcel } from '@/assets/js/custom';

const filtroPadrao = () => ({ doadorId: null, responsavelId: null, probabilidade: null, statusId: null, fonteId: null, incentivoId: null, previsaoInicio: null, previsaoFim: null, atividadesAtrasadas: false });
const etapasPadrao = [
  { probabilidade: 1, nome: 'Prospecção', percentual: 0 },
  { probabilidade: 2, nome: 'Participação em edital', percentual: 20 },
  { probabilidade: 3, nome: 'Reunião agendada', percentual: 40 },
  { probabilidade: 4, nome: 'Revisão do projeto', percentual: 60 },
  { probabilidade: 5, nome: 'Negociação', percentual: 80 },
  { probabilidade: 6, nome: 'Fechamento', percentual: 100 }
].map(e => ({ ...e, rotulo: `${e.nome} ${e.percentual}%` }));

export default {
  data: () => ({
    service: new OportunidadeService(), filtro: filtroPadrao(), search: '', buscaDoador: null,
    doadores: [], responsaveis: [], fontes: [], incentivos: [], carregandoDoadores: false,
    oportunidades: [], etapas: etapasPadrao.map(e => ({ ...e, quantidade: 0, valorTotal: 0 })), etapasPadrao,
    indicadores: {}, visualizacao: 'lista', loading: false, movimentando: false, arrastando: null,
    erroCarga: '', requisicao: 0, requisicaoDoador: 0, dialogCadastro: false, item: new Oportunidade(),
    cores: ['#b9cfdf', '#c9c5e3', '#add5d1', '#e8d2b2', '#c8d9af', '#adcabc'],
    status: [{ id: 'A', nome: 'Aberto' }, { id: 'F', nome: 'Fechado' }, { id: 'C', nome: 'Cancelado' }],
    headers: [
      { text: '', value: 'actions', sortable: false }, { text: 'Descrição', value: 'descricao' },
      { text: 'Doador', value: 'doadorNome' }, { text: 'Valor', value: 'valorTotal' },
      { text: 'Prob. %', value: 'probabilidade' }, { text: 'Responsável', value: 'responsavelNome' },
      { text: 'Previsão', value: 'previsaoFechamento' }, { text: 'Próxima atividade', value: 'proximaAtividade', sortable: false },
      { text: 'Fonte', value: 'fonte' }, { text: 'Status', value: 'status' },
      { text: 'Lei Incentivo', value: 'leiIncentivo' }, { text: 'Anexo', value: 'anexo', sortable: false }
    ], tableToExcel: TableToExcel
  }),
  computed: {
    cardsIndicadores() {
      return [
        { nome: 'Oportunidades abertas', valor: this.indicadores.oportunidadesAbertas || 0 },
        { nome: 'Valor total em aberto', valor: this.Moeda(this.indicadores.valorTotalAberto) },
        { nome: 'Valor ponderado', valor: this.Moeda(this.indicadores.valorPonderado) },
        { nome: 'Fechamentos em 30 dias', valor: this.indicadores.fechamentosPrevistos || 0 },
        { nome: 'Atividades atrasadas', valor: this.indicadores.atividadesAtrasadas || 0 }
      ];
    },
    dadosFunil() {
      const maior = Math.max(0, ...this.etapas.map(e => e.valorTotal));
      return this.etapas.map(e => [(maior - e.valorTotal) / 2, (maior + e.valorTotal) / 2, e.valorTotal]);
    }
  },
  watch: {
    async buscaDoador(valor) {
      const requisicao = ++this.requisicaoDoador;
      if (!valor || valor.length < 2) { this.carregandoDoadores = false; return; }
      this.carregandoDoadores = true;
      try {
        const res = await new DoadorService().AutoComplete(encodeURIComponent(valor));
        if (requisicao === this.requisicaoDoador) this.doadores = res.data;
      } catch (err) {
        if (requisicao === this.requisicaoDoador) this.Erro(err);
      } finally {
        if (requisicao === this.requisicaoDoador) this.carregandoDoadores = false;
      }
    }
  },
  created() {
    this.Listar();
    Promise.all([this.service.Responsaveis(), new OportunidadeFonteEnumService().Listar(), new OportunidadeLeiIncentivoEnumService().Listar()])
      .then(([usuarios, fontes, incentivos]) => { this.responsaveis = usuarios.data; this.fontes = fontes.data; this.incentivos = incentivos.data; })
      .catch(this.Erro);
  },
  beforeDestroy() { this.requisicao++; this.requisicaoDoador++; },
  methods: {
    async Listar() {
      if (this.filtro.previsaoInicio && this.filtro.previsaoFim && this.filtro.previsaoInicio > this.filtro.previsaoFim) {
        this.$swal('Aviso', 'A data inicial deve ser anterior ou igual à data final.', 'warning'); return;
      }
      const requisicao = ++this.requisicao;
      this.loading = true; this.erroCarga = '';
      try {
        const filtro = { ...this.filtro, previsaoInicio: this.filtro.previsaoInicio || null, previsaoFim: this.filtro.previsaoFim || null, busca: this.search || null };
        const res = await this.service.Painel(filtro);
        if (requisicao !== this.requisicao) return;
        this.oportunidades = res.data.oportunidades;
        this.etapas = res.data.etapas;
        this.indicadores = res.data.indicadores;
      } catch (err) {
        if (requisicao === this.requisicao) this.erroCarga = this.MensagemErro(err);
      } finally {
        if (requisicao === this.requisicao) this.loading = false;
      }
    },
    LimparFiltros() { this.filtro = filtroPadrao(); this.Listar(); },
    Novo() { this.item = new Oportunidade(); this.dialogCadastro = true; },
    async Editar(card) {
      try { const res = await this.service.ObterPorId(card.id, 'Doador.Contatos'); this.item = new Oportunidade(res.data); this.dialogCadastro = true; }
      catch (err) { this.Erro(err); }
    },
    async Excluir(card) {
      const resultado = await this.$swal({ title: 'Atenção!', text: 'Tem certeza que deseja excluir a captação?', type: 'question', showCancelButton: true, confirmButtonText: 'Sim', cancelButtonText: 'Não' });
      if (!resultado.value) return;
      try { await this.service.Excluir(card.id); await this.Listar(); } catch (err) { this.Erro(err); }
    },
    Arrastar(evento, card) {
      if (this.movimentando) { evento.preventDefault(); return; }
      this.arrastando = card;
      evento.dataTransfer.effectAllowed = 'move'; evento.dataTransfer.setData('text/plain', String(card.id));
    },
    Soltar(probabilidade) { if (this.arrastando) this.Mover(this.arrastando, probabilidade); this.arrastando = null; },
    async Mover(card, probabilidade) {
      if (this.movimentando || this.loading || card.probabilidade === probabilidade) return;
      this.movimentando = true;
      try { await this.service.MoverEtapa(card.id, { probabilidade, versao: card.versao }); await this.Listar(); }
      catch (err) { this.Erro(err); if (err.response && err.response.status === 409) await this.Listar(); }
      finally { this.movimentando = false; }
    },
    CardsEtapa(probabilidade) { return this.oportunidades.filter(o => o.probabilidade === probabilidade); },
    NomeEtapa(probabilidade) { const etapa = etapasPadrao.find(e => e.probabilidade === probabilidade); return etapa ? etapa.rotulo : '—'; },
    NomeEnum(itens, chave) { const valor = itens.find(i => i.key === chave); return valor ? valor.value : '—'; },
    NomeStatus(valor) { const status = this.status.find(s => s.id === valor); return status ? status.nome : valor; },
    Moeda(valor) { return Number(valor || 0).toMoeda(true); },
    Data(valor) { return valor ? valor.slice(0, 10).split('-').reverse().join('/') : '—'; },
    Download(card) { this.service.DownloadDocumento(card.id, card.anexo).catch(this.Erro); },
    MensagemErro(err) { const dados = err.response && err.response.data; return typeof dados === 'string' ? dados : (dados && (dados.detail || dados.title)) || 'Não foi possível concluir a operação. Tente novamente.'; },
    Erro(err) { this.$swal('Aviso', this.MensagemErro(err), err.response && err.response.status === 409 ? 'warning' : 'error'); }
  }
};
</script>
<style scoped>
.indicadores { display: flex; flex-wrap: wrap; gap: 12px; }
.indicador { flex: 1 1 170px; }
.kanban { display: flex; align-items: stretch; gap: 12px; overflow-x: auto; padding-bottom: 16px; }
.kanban-coluna { flex: 0 0 285px; border: 1px solid rgba(128,128,128,.25); border-top: 5px solid; border-radius: 5px; background: rgba(128,128,128,.04); min-height: 320px; }
.kanban-card { cursor: grab; }
.kanban-card:active { cursor: grabbing; }
.captacao-titulo { flex: 1; overflow-wrap: anywhere; }
</style>
